import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Column } from 'primereact/column'
import { Dropdown } from 'primereact/dropdown'
import { InputTextarea } from 'primereact/inputtextarea'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import Button from '../../components/ui/Button'
import { useToast } from '../../components/ui/ToastProvider'
import { useCurrentUser, useGrants } from '../../auth/useCurrentUser'
import { GRANTS, SUBACTION_CREATE_GRANTS } from '../../auth/grants'
import { playSuccessSound } from '../../lib/sound'
import {
  deadlineMinDate,
  formatDate,
  parseISODate,
  todayStart,
  toISODate,
} from '../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { deleteLabel } from '../../lib/deleteLabel'
import SubActionCards from './components/SubActionCards'
import { useSetBreadcrumb, type Crumb } from '../../app/nav/breadcrumb'
import {
  useActionDetail,
  useAddAssignee,
  useAllowedTransitions,
  useChangeActionStatus,
  useCreateSubAction,
  useDeleteActionNote,
  useDeleteSubAction,
  useRemoveAssignee,
  useSetMainResponsible,
  useUpdateAction,
  useUpdateActionNote,
  useUploadAttachments,
} from './hooks/useActionDetail'
import { recordActionViewAccess } from './api/actionApi'
import ActionTerminCard from './components/ActionTerminCard'
import {
  ActionNotesSection,
  NoteEditDialog,
} from './components/ActionNotesSection'
import DeadlineRequestDialog from './components/DeadlineRequestDialog'
import { useCreateDeadlineRequest } from './hooks/useCreateDeadlineRequest'
import { useNavigateWithOrigin } from '../../app/nav/originNav'
import ActionCompletionNote from './components/ActionCompletionNote'
import ActionInfoGrid from './components/ActionInfoGrid'
import ActionStatusTag from './components/ActionStatusTag'
import ActionPeopleEditor from './components/ActionPeopleEditor'
import SinglePersonEditor from './components/SinglePersonEditor'
import ActionDocumentsSection from './components/ActionDocumentsSection'
import SubActionDialog from './components/SubActionDialog'
import ActionHistoryDialog from './components/ActionHistoryDialog'
import CompletionNoteDialog from './components/CompletionNoteDialog'
import TaskClassificationFields from './components/TaskClassificationFields'
import { useActionLevels } from './hooks/useActionLevels'
import {
  assigneeAddErrorKey,
  assigneeRemoveErrorKey,
  mainResponsibleSetErrorKey,
  statusChangeErrorKey,
  subActionCreateErrorKey, subActionDeleteErrorKey, noteMutationErrorKey,
  documentMutationErrorKey } from './statusError'
import { useActionStatusName } from './hooks/useActionStatuses'
import { useProxyMode } from '../proxy/useProxyMode'
import {
  ACTION_STATUS,
  ASSIGNEE_TYPE,
  isMainResponsible as isMainResponsibleOf,
  baseStatusOf,
  classificationIds,
  EMPTY_CLASSIFICATION,
  mainResponsibleOf,
  secondaryResponsiblesOf,
  type ActionDetail,
  type ActionNote,
  type PersonInput,
  type SubAction,
  type TaskClassification,
} from './types'
import '../minutes/minutes.css'
import '../meetings/meetings.css'
import './actions.css'

/**
 * Salt-okuma sayılan ana aksiyon statüleri (085): <b>Kapatıldı</b> ve <b>Arşivlendi</b>. Tamamlandı
 * BURADA DEĞİLDİR — yeni modelde Tamamlandı'dan Aksiyon Bekleniyor'a geri dönülebilir (FR-005).
 */
function isTerminalStatus(statusId: number | null) {
  return statusId === ACTION_STATUS.CLOSED || statusId === ACTION_STATUS.ARCHIVED
}

/**
 * Termini geçmiş mi? (Gecikti — yalnız aksiyonun kendi termininden türetilir.)
 *
 * <p>Karşılaştırma <b>kurumsal güne</b> ({@link todayStart} → Europe/Istanbul) göredir, tarayıcının
 * yerel gününe değil: backend'in aynı kuralı ({@code ActionOverdueRule.isOverdue}) o günü kullanır.
 * Ayrışırsa, ör. gecikmiş bir ana aksiyonda "Alt Aksiyon Ekle" butonu aktif görünürken uç
 * {@code 400 parentOverdue} döner.
 */
function isPastDeadline(deadline: string | null | undefined) {
  const d = parseISODate(deadline ?? '')
  if (!d) return false
  return d < todayStart()
}

/**
 * Devam eden (açık) alt aksiyonların en büyük termini; yoksa null. Ana aksiyon termini bundan önce seçilemez
 * (alt ≤ ana değişmezi) — bitmiş alt aksiyonlar ana'yı kısıtlamaz. "Açık" kümesi backend
 * {@code SUBACTION_OPEN_STATUSES} ile birebir: 22.09.2026'dan beri yalnız <b>Aksiyon Bekleniyor(330)</b>
 * (Devam Ediyor/332 alt görevin statü kümesinden kalktı; Tamamlandı/Kapatıldı ana'yı kısıtlamaz).
 *
 * <p>121 — toplantı bağımlı görevde <b>otomatik</b> alt görevler sayılmaz: tarih aynı kayıtta onlara da
 * yazılır. Backend karşılığı {@code AutoSubActionPropagationService.followsParent}.
 */
function openSubActionsMaxDeadline(
  subActions: SubAction[] | undefined,
  meetingBound: boolean,
): Date | null {
  const open: number[] = [ACTION_STATUS.PENDING]
  let max: Date | null = null
  for (const s of subActions ?? []) {
    if (meetingBound && !s.manual) continue
    if (s.statusId == null || !open.includes(s.statusId)) continue
    const d = parseISODate(s.deadline ?? '')
    if (d && (!max || d > max)) max = d
  }
  return max
}

/**
 * Statü DEĞİŞTİRME dropdown'unun HEDEFLERİ — <b>mevcut statü DAHİL DEĞİL</b>: dropdown bir "Durum
 * değiştir" aksiyonudur (placeholder), güncel statü "Aksiyon Bilgileri > Durum"da görünür.
 *
 * <p><b>2026-08-13:</b> burada eskiden yönetim-dışı kullanıcının hedefleri <b>tek bir statüye</b>
 * indiriliyordu ("sorumlu yalnız Güncellendi'ye çeker" kuralının ekran kopyası). O kural uçtan
 * kaldırıldı; kopya yerinde kalınca <b>Ana Sorumlu kendi aksiyonunu ekrandan hiç değiştiremiyordu</b>
 * — dropdown boş kalıp hiç çizilmiyordu (bağımsız code-review bulgusu).
 *
 * <p>Artık hedefler <b>uçtan geldiği gibi</b> gösterilir: "kim neyi yapabilir" kararı tek yerde,
 * {@code ActionCommandService}'te verilir. Yetkisiz bir seçim yine 403 döner — ama kullanıcı en azından
 * seçeneği görür ve reddin gerekçesini alır.
 */
function visibleStatusTargets(statusId: number | null, allowed: number[] | undefined) {
  return statusId != null ? (allowed ?? []) : []
}

/**
 * Breadcrumb: toplantıya bağlı aksiyonda önce toplantı (tıklanınca Toplantı Detay'a döner), sonra "Aksiyon
 * Detay"; bağımsız aksiyonda yalnız "Aksiyon Detay". Toplantı bağlamı detay verisinden türetilir (ek
 * nav-state yok).
 */
function breadcrumbSteps(data: ActionDetail | undefined, detailTitle: string): Crumb[] {
  if (data?.meetingId != null && data.meetingName) {
    return [
      { label: data.meetingName, to: `/minutes/meeting-minutes/meetings/${data.meetingId}` },
      { label: detailTitle },
    ]
  }
  return [{ label: detailTitle }]
}

/** Detaydaki Stratejik Hedef / Katkı seçimi (127) — eski backend alanı göndermezse boş. */
function classificationOf(data: ActionDetail): TaskClassification {
  return { strategicObjectives: data.strategicObjectives ?? [], contributions: data.contributions ?? [] }
}

/**
 * Görev Açıklaması bölümündeki Stratejik Hedef / Katkı (127). Sayfa bileşeninden ayrıldı: koşullu çizim
 * orada bilişsel karmaşıklığa yazılırdı ve bileşen Sonar eşiğinin sınırında. Alt görevde çizilmez (alt
 * görevin seçimi yoktur). Seçenekler yalnız düzenlenebilirken çekilir — salt-okumada seçili kalemler zaten
 * değerin içindedir ve alan devre dışıdır.
 */
function DetailClassificationFields({
  visible,
  editable,
  value,
  onChange,
}: Readonly<{
  visible: boolean
  editable: boolean
  value: TaskClassification
  onChange: (next: TaskClassification) => void
}>) {
  const { data: levels } = useActionLevels(visible && editable)
  if (!visible) return null
  return (
    <div className="act-desc-grid act-desc-classification">
      <TaskClassificationFields
        options={levels}
        value={value}
        onChange={onChange}
        disabled={!editable}
        fullWidth={false}
      />
    </div>
  )
}

/**
 * Rol + statüden türeyen düzenleme/statü yetkileri — tek yerde, saf. Sayfa bileşeninin içinde durduğunda
 * her biri ayrı bir koşul olarak bileşenin bilişsel karmaşıklığına yazılıyordu (Sonar S3776).
 */
function derivePermissions(
  data: ActionDetail | undefined,
  employeeId: string | null | undefined,
  canManage: boolean,
  canManageStatus: boolean,
  /** Kısıtlı Yetkili asistan kipi — her yazma yüzeyini kapatır (126 FR-019). */
  readOnly: boolean,
) {
  const statusId = data?.statusId ?? null
  const terminal = isTerminalStatus(statusId)
  // ActionDetailPage yalnız KÖK aksiyonları render eder (alt aksiyon ayrı sayfaya redirect edilir).
  const isSubAction = data?.parentId != null
  /**
   * **Ana Sorumlu** mu? Sunucudaki {@code ActionCommandService#isMainResponsible} ölçütünün aynası:
   * sicil eşleşen **ve rolü Ana(340) olan** atama satırı. Rolsüz (null) satır Ana **sayılmaz** —
   * sunucudaki {@code MinutesAssigneeType.isMain} de saymaz, iki taraf bilinçle aynı.
   *
   * <p><b>2026-08-30'da düzeltildi (kullanıcı bildirimi).</b> Eskiden ölçüt "atananlardan biri"ydi ve
   * adı `isOwner`'dı; sunucu ise ana görevin statüsünü **yalnız** Ana Sorumlu'ya (veya yönetime)
   * açıyor. Ayrışmanın iki yönü de hatalıydı:
   * <ol>
   *   <li><b>Dar:</b> koşul `isOwner && isOverdue` idi → Ana Sorumlu, <b>gecikmemiş</b> görevinde
   *       statü listesini hiç göremiyordu. Oysa sunucu ({@code requireManagement}) gecikme şartı
   *       aramaz; `requireDelayedExitRight` bile aynı kümeye delege eder.
   *   <li><b>Geniş:</b> "herhangi bir atanan" ölçütü <b>Yan Sorumlu</b>'yu da kapsıyordu → gecikmiş
   *       görevde listeyi görüp seçince uçtan 403 alırdı. Sunucuda bu bir code review'de kapatılmış
   *       ({@code requireDelayedExitRight} yorumu), istemci güncellenmemişti.
   * </ol>
   * Ad da `isOwner`'dan çevrildi: "owner" role bakmadığını gizliyordu ve hatanın kaynağı buydu.
   */
  // Paylaşılan yardımcı (`types.ts`) — inline kopya yazmak onu GÖLGELER ve ikisi zamanla ayrışır
  // (push kapısı review bulgusu). Ölçüt sunucudaki `isMainResponsible`/`isMain` ile birebir aynı.
  const isMainResponsible = !!employeeId && isMainResponsibleOf(data?.assignees, employeeId)
  const isOverdue = isPastDeadline(data?.deadline)
  /**
   * Kimlik alanlarının kayıt-düzeyi izni SUNUCUDAN gelir (`editable` emsali). İstemci bunu yeniden
   * hesaplaMAZ: kuralın "görevi **oluşturan** muaftır" kolu (kullanıcı kuralı 2026-08-31) burada
   * ölçülemez — yanıtta yalnız oluşturanın görünen adı var ve ada göre kıyas kırılgandır. Alan
   * gelmezse **fail-closed** (aynı desen: `serverEditable`).
   */
  const serverInfoEditable = data?.infoEditable ?? false
  /** Hedef teslim tarihi — aynı desen, fail-closed (130: `ACTION_DEADLINE_UPDATE` ∨ oluşturan). */
  const serverDeadlineEditable = data?.deadlineEditable ?? false
  const fieldsEditable = canManage && !terminal && !isSubAction
  return {
    statusId,
    isSubAction,
    isMainResponsible,
    isOverdue,
    editable: canManage && !terminal,
    // Statü değiştirme = sunucunun `requireManagement` kapısının aynası: YÖNETİM veya ANA SORUMLU.
    // Gecikme şartı YOK (2026-08-30 — eskiden `isOwner && isOverdue` idi, bkz. isMainResponsible).
    // Terminal → salt-okuma. Gecikmiş görevde ayrıca ZORUNLU not/termin akışı devreye girer, ama
    // o ayrı bir kural: listenin görünürlüğünü belirlemez.
    // ⚠️ `!readOnly` ÜÇÜNCÜ koşuldur ve `||`'ın DIŞINDA durmak zorundadır: `isMainResponsible`
    // bir İLİŞKİ olgusudur, yetki değil, ve persona kipinde `/me.employeeId` HEDEFİN sicilidir —
    // yani asistan, yöneticinin Ana Sorumlu olduğu her görevde o kolu sağlıyor ve `canManageStatus`
    // tamamen atlanıyordu. Kısıtlı Yetkide "Onaya Gönder" ve statü listesi çiziliyor, basınca
    // sunucu 403 veriyordu (push kapısı review'ü, 20.10.2026 — depodaki `setExecutiveGrants`
    // testi bu kolu zaten kanıtlıyordu).
    canChangeStatus: !terminal && (canManageStatus || isMainResponsible) && !readOnly,
    // Termin alanının ÇİZİLMESİ: alt aksiyonda kapalı (salt-okuma). Görev Bilgileri'nin KİMLİK
    // alanları buradan AYRILDI (2026-08-31) — bkz. infoEditable.
    fieldsEditable,
    /**
     * Termin alanının DÜZENLENEBİLMESİ (130, kullanıcı kararı 2026-11-02): yalnız
     * `ACTION_DEADLINE_UPDATE` yetkisi taşıyan ya da görevi **oluşturan** kullanıcı; rol, atanan olmak
     * ve `MINUTES_CREATE` tek başına yetmez. Yetkisiz atananın yolu termin güncelleme talebidir (087).
     * Ölçüt sunucudadır ({@code ActionDeadlinePolicy}); uç aynı metotla kapılanır. (2026-10-02 kuralı:
     * yönetim ∨ oluşturan ∨ atanan değil.)
     */
    deadlineEditable: fieldsEditable && serverDeadlineEditable,
    /**
     * **Görev Bilgileri — kimlik alanları:** Ana Sorumlu, Süreç Destek Yöneticisi, başlık, detay.
     * (Takip Eden 120'ye kadar aynı kuraldaydı; artık herkese salt-okumadır — kişi kendini yıldızla
     * ekler.)
     *
     * <p><b>Görevin ANA SORUMLUSU bu alanları değiştiremez</b> (kullanıcı kuralı 2026-08-31).
     * Ana Sorumlu o kayıtta şunları yapabilir: statü güncelleme, hedef teslim tarihi güncelleme
     * **talebi**, not ekleme, alt görev oluşturma, doküman ekleme/silme. Bunların hiçbiri bu bayrağa
     * bağlı değildir — kural yalnız kimlik alanlarını kapatır. (Termini doğrudan güncelleme kendi
     * kuralına tabidir — bkz. `deadlineEditable`.)
     *
     * <p><b>Neden `canManage` yetmiyordu:</b> ölçüt `ACTION_MANAGE`'dir ve o yetki **Yönetici/GMY
     * rollerinde de vardır** (075 seed); ana görevin yazma kapsamı da zaten sorumluluk üzerinden
     * açılır. Sonuç: görevin Ana Sorumlusu olan bir Yönetici kendi görevinin başlığını, detayını ve
     * sorumlularını değiştirebiliyordu. Aynı sınıf hata alt görevde 2026-08-30'da düzeltilmişti; bu,
     * onun ana görev karşılığıdır.
     *
     * <p><b>İki muafiyet, ikisi de sunucuda ölçülür</b> ({@code serverInfoEditable}): (1) **yönetim**
     * (`MINUTES_CREATE`) — Özel Kalem bir görevin Ana Sorumlusu olsa da düzenler; (2) görevi
     * **oluşturan** — kullanıcı kuralı 2026-08-31, *"Görevi Oluşturan; Görevde herhangi bir değişiklik
     * yapabilir"*. Kısıt bir ROL kısıtı değil: tanımı KOYAN ile tanımı UYGULAYAN'ı ayırır.
     *
     * <p>Aynı kural <b>sunucuda da</b> zorlanır ({@code ActionDetailService#isTaskInfoEditable} — hem
     * uçların kapısı hem bu bayrağın kaynağı): yalnız arayüzü kapatmak, uca doğrudan istek atan bir
     * yolu kuralın dışında bırakırdı — 2026-08-30'da tam olarak bu ayrışma belgelenmişti (bkz.
     * 066/spec.md Session 2026-08-30 uyarı kutusu).
     *
     * <p><b>Kapsam notu:</b> aşağıdaki `!terminal` koşulunun sunucu karşılığı YOKTUR — sonlanmış
     * görevde bu uçlar hâlâ 200 döner (bağımsız review bulgusu 2026-08-31, önceden de böyleydi).
     * Yön ekran-kapalı/uç-açık olduğu için sahte 403 üretmez; kapatılacaksa ayrı bir karardır.
     */
    infoEditable: canManage && !terminal && !isSubAction && serverInfoEditable,
    // Takip Eden bayrağı (`followersEditable`) 120'de KALKTI: bölüm herkese salt-okumadır.
    /**
     * 085/FR-008 — YAPI değişikliği (alt aksiyon ekleme, Yan Sorumlu ekleme/silme) yalnız aksiyon
     * <b>Aksiyon Bekleniyor</b> iken yapılabilir. `editable`den ayrıdır: Tamamlandı'da alan düzenleme
     * hâlâ açıktır (o statüden geri dönülebilir), ama yapı dondurulur. Uç de aynı kuralı zorlar
     * (`actionNotPending`) — burası yalnız butonu baştan pasif göstermek içindir.
     */
    structurallyEditable: canManage && !isSubAction && statusId === ACTION_STATUS.PENDING,
    /**
     * Hedef teslim tarihi güncelleme talebi (087) açılabilir mi. Ölçüt **sunucudan** gelir ve ilişki
     * tabanlıdır (yöneticinin kendi görevinde {@code true}); uç {@code HAS_ACTION_READ_ANY} ile
     * korunuyor, yani **okuma kodlarıyla açılan bir kayıt oluşturma ucu** — yetki listesi burada
     * hiçbir şey yapamaz, kipin sorulması şart (research D14).
     *
     * <p>Koşul bileşenin içinde değil BURADA kuruluyor: orada yazıldığında ana bileşenin bilişsel
     * karmaşıklığını 15'ten 16'ya çıkarıp Sonar S3776'yı tetikledi (ölçüldü, tahmin değil).
     */
    deadlineRequestAllowed: (data?.deadlineRequestAllowed ?? false) && !readOnly,
  }
}

/**
 * Gecikti-çıkışın REF'teki AÇIK hedefleri: "Aksiyon Bekleniyor"(330) [yalnız Özel Kalem] ve
 * "Güncellendi"(331). Bu hedeflere geçişte yeni termin popup'ta alınır.
 */
const OVERDUE_EXIT_TARGETS: number[] = [ACTION_STATUS.PENDING]

/**
 * Gecikti'den çıkış popup'ının doğrulaması. Her iki rol için yeni termin zorunludur (bugün+).
 *
 * <p><b>ÖNCELİK YÖNETİMDEDİR</b> (kullanıcı kararı 2026-08-10; uçtaki {@code ownerNoteOutcome} ile
 * birebir aynı sıra): kullanıcı hem yönetim (Özel Kalem) hem aksiyonun sorumlusu olabilir ve o durumda
 * not <b>isteğe bağlıdır</b>. Sıra ters olursa — bu ekranda uzun süre öyleydi — yetkili bir sorumlu
 * kendi aksiyonunu terminden çıkarırken zorunlu nota takılır ve istek uca hiç gitmez.
 */
function overdueExitError(
  exitDeadline: string,
  isMainResponsible: boolean,
  canManageStatus: boolean,
  exitNote: string,
) {
  if (exitDeadline === '') return 'actions.detail.overdueExit.deadlineRequired'
  if (canManageStatus) return null
  // Sunucu `noteOutcome`: yönetim → not opsiyonel; yönetim-DIŞI Ana Sorumlu → not ZORUNLU.
  if (isMainResponsible && exitNote.trim() === '') return 'actions.detail.overdueExit.noteRequired'
  return null
}

/**
 * Alan güncellemesinin doğrulaması: tanım/açıklama/termin zorunlu; termin değiştiyse not da zorunlu (7.8).
 * Hata varsa i18n anahtarı, yoksa {@code null} döner.
 */
function updateError(
  name: string,
  description: string,
  deadline: string,
  deadlineChanged: boolean,
  noteText: string,
) {
  if (name.trim() === '' || description.trim() === '' || deadline === '') {
    return 'actions.detail.update.invalid'
  }
  if (deadlineChanged && noteText === '') return 'actions.detail.notes.deadlineRequired'
  return null
}



/** İki termin popup'ının paylaştığı görünüm sözleşmesi. */
type DeadlineDialogProps = {
  visible: boolean
  deadline: string
  minDate: Date
  pending: boolean
  onDeadline: (iso: string) => void
  onSubmit: () => void
  onClose: () => void
}

/**
 * Popup'ların ortak Kaydet ayağı — üç yerde aynı olduğu için tek bileşen.
 *
 * <p>İptal butonu KALDIRILDI (kullanıcı kararı 2026-08-28, uygulama geneli): vazgeçme yalnız
 * başlıktaki × ile. {@code onClose} de bu yüzden artık gerekmiyor — pop-up'ı kapatma işini
 * Dialog'un kendi {@code onHide}'ı yapıyor.
 */
function DialogFooter({
  pending,
  onSubmit,
}: Readonly<Pick<DeadlineDialogProps, 'pending' | 'onSubmit'>>) {
  const { t } = useTranslation()
  return (
    <div className="ui-dialog-footer">
      <Button
        icon="pi pi-check"
        label={t('actions.detail.overdueExit.save')}
        onClick={onSubmit}
        loading={pending}
      />
    </div>
  )
}

/** "Yeni termin" alanı — iki popup'ta birebir aynı (bugün+ alt sınırı çağırandan gelir). */
function DeadlineField({
  deadline,
  minDate,
  onDeadline,
}: Readonly<Pick<DeadlineDialogProps, 'deadline' | 'minDate' | 'onDeadline'>>) {
  const { t } = useTranslation()
  return (
    <label className="act-field act-field--full">
      <span className="ui-field-label">
        {t('actions.detail.overdueExit.deadlineLabel')} <span className="ui-req">*</span>
      </span>
      <Calendar
        value={parseISODate(deadline)}
        minDate={minDate}
        dateFormat="dd.mm.yy"
        showIcon
        onChange={(e) => onDeadline(toISODate(e.value ?? null))}
      />
    </label>
  )
}

/**
 * Gecikti aksiyonu → AÇIK statü (belge kuralı): YÖNETİM zorunlu yeni TERMİN (temiz Güncellendi), SORUMLU
 * ayrıca zorunlu NOT (Gecikti-Güncellendi). Role göre not alanı gösterilir. Sayfa JSX'inden ÇIKARILDI:
 * koşullu render'lar bileşenin bilişsel karmaşıklığına yazılıyordu (Sonar S3776).
 */
function OverdueExitDialog({
  visible,
  noteRequired,
  deadline,
  note,
  minDate,
  pending,
  onDeadline,
  onNote,
  onSubmit,
  onClose,
}: DeadlineDialogProps & { noteRequired: boolean; note: string; onNote: (v: string) => void }) {
  const { t } = useTranslation()
  return (
    <Dialog
      visible={visible}
      header={t('actions.detail.overdueExit.title')}
      modal
      dismissableMask
      draggable={false}
      style={{ width: '38rem', maxWidth: '95vw' }}
      onHide={onClose}
      footer={<DialogFooter pending={pending} onSubmit={onSubmit} />}
    >
      <p className="act-empty-hint">
        {noteRequired
          ? t('actions.detail.overdueExit.hintOwner')
          : t('actions.detail.overdueExit.hintManager')}
      </p>
      <div className="act-form">
        <DeadlineField deadline={deadline} minDate={minDate} onDeadline={onDeadline} />
        {/* Gerekçe (not): ATANAN (sorumlu) için ZORUNLU (→ Gecikti-Güncellendi); ÖZEL KALEM (yönetim) için
            İSTEĞE BAĞLI — doldurulursa kalıcı yazılır ama "temiz Güncellendi" kalır. Alan her iki rolde de
            gösterilir; yıldız yalnız zorunlu (sorumlu) durumda. */}
        <label className="act-field act-field--full">
          <span className="ui-field-label">
            {t('actions.detail.overdueExit.noteLabel')}
            {noteRequired && <span className="ui-req"> *</span>}
          </span>
          <InputTextarea
            value={note}
            rows={3}
            autoResize
            maxLength={4000}
            onChange={(e) => onNote(e.target.value)}
          />
        </label>
      </div>
    </Dialog>
  )
}

/*
 * `AssigneeDeadlineDialog` KALDIRILDI (kullanıcı kuralı 2026-08-17): 066/AK-12'nin "geciken aksiyona
 * yeni termin + gerekçe vererek sorumlu ekle" akışı kapandı — geciken aksiyona Yan Sorumlu hiç
 * eklenemiyor, kullanıcı uyarı alıp önce hedef teslim tarihini güncelliyor. Pop-up'ın girdisi kalmadığı
 * için bileşen de kaldırıldı; `OverdueExitDialog` (statü değişiminde termin) yerinde durur.
 */

/** Sayfanın bileşen gövdesinden çıkarılan akışların paylaştığı bağımlılıklar. */
type PageDeps = {
  data: ActionDetail | undefined
  isOverdue: boolean
  toast: ReturnType<typeof useToast>
  t: ReturnType<typeof useTranslation>['t']
}

/**
 * <b>Gecikti-çıkış</b> popup'ının durumu ve akışı. Bileşen gövdesinden ÇIKARILDI: üç {@code useState} +
 * dallanmalı üç işleyici, bileşenin bilişsel karmaşıklığına yazılıyor ve Sonar S3776 eşiğini aşıyordu.
 * Dışa verilen adlar bileşendeki eski yerel adlarla <b>birebir aynıdır</b> — JSX değişmedi.
 */
function useOverdueExit(
  deps: PageDeps & {
    isMainResponsible: boolean
    canManageStatus: boolean
    changeStatus: ReturnType<typeof useChangeActionStatus>
    /** Onaya gönderme pop-up'ında seçilen dosyaları yükler (statü YAZILDIKTAN sonra). */
    uploadAttachments: ReturnType<typeof useUploadAttachments>
  },
) {
  const {
    data,
    isOverdue,
    isMainResponsible,
    canManageStatus,
    changeStatus,
    uploadAttachments,
    toast,
    t,
  } = deps
  const [exitTarget, setExitTarget] = useState<number | null>(null)
  const [exitDeadline, setExitDeadline] = useState('')
  const [exitNote, setExitNote] = useState('')
  /** 105 — onaya gönderme notu pop-up'ı açık mı (yalnız 331 hedefinde). */
  const [completionNoteOpen, setCompletionNoteOpen] = useState(false)

  const doChangeStatus = (
    to: number,
    newDeadline?: string,
    statusNote?: string,
    completionNote?: string | null,
    files?: File[],
  ) => {
    if (!data) return
    changeStatus.mutate(
      {
        toStatusId: to,
        modDate: data.modDate,
        newDeadline: newDeadline ?? null,
        note: statusNote ?? null,
        // 105 — yalnız Tamamlandı hedefinde dolu gelir; `undefined` ise alan gövdeye hiç girmez
        // ve sunucu mevcut notu KORUR (bu yol 331 dışındaki hedefler içindir).
        completionNote,
      },
      {
        onSuccess: () => {
          setExitTarget(null)
          if (to === ACTION_STATUS.COMPLETED) playSuccessSound()
          toast.success(t('actions.detail.status.changed'))
          uploadSelectedFiles(files)
        },
        onError: (err) => toast.error(t(statusChangeErrorKey(err))),
      },
    )
  }

  /**
   * Onaya gönderme pop-up'ında seçilen dosyaları yükler — statü **yazıldıktan sonra** (30.09.2026).
   *
   * <p><b>Sıra bilinçlidir:</b> statü yazılamazsa (bayat `modDate` → 409, yetki → 403) görev eskisi
   * gibi kalmalıdır; dosyayı önce yüklemek, gerçekleşmeyen bir gönderime ait ekler bırakırdı.
   *
   * <p><b>Dosya hatası statü değişikliğini GERİ ALMAZ</b> ve onu başarısız göstermez: statü zaten
   * yazıldı, geri alınamaz. Kullanıcı ayrı bir uyarı görür ve dosyaları Görev Detay'daki Dokümanlar
   * bölümünden ekleyebilir — bağımsız görev oluşturmadaki `filesFailed` yolunun aynı ilkesi.
   */
  const uploadSelectedFiles = (files?: File[]) => {
    if (!files || files.length === 0) return
    uploadAttachments.mutate(files, {
      onError: (e) =>
        toast.error(t(documentMutationErrorKey(e, 'actions.detail.documents.uploadError'))),
    })
  }

  // Gecikti (termini geçmiş) aksiyonu AÇIK bir statüye çekiş → popup'ta ROL bazında alan alınır. REF geçişleri:
  // Bekleniyor(330) [yalnız Özel Kalem] ve Güncellendi(331). Her iki hedefte de yeni termin zorunlu; ATANAN
  // (sorumlu) ise AYRICA zorunlu not (Gecikti-Güncellendi → IS_DELAYED_UPDATED=Evet). Diğer geçişler doğrudan.
  const onStatusChange = (to: number) => {
    // `to` TEMEL hedef id (dropdown mevcut statüyü göstermez → seçim daima gerçek geçiştir). Devam Eden
    // (Gecikmeli, 332) → Devam Eden (330) de artık gerçek geçiştir: base çakışması nedeniyle eskiden
    // engelleniyordu; mevcut statü dropdown değeri olmadığından o kısıt kaldırıldı (aşağıdaki overdue-exit
    // yolu 330/331 için yeni termin popup'ını açar).
    if (!data) return
    if (isOverdue && OVERDUE_EXIT_TARGETS.includes(to)) {
      setExitTarget(to)
      setExitDeadline('')
      setExitNote('')
      return
    }
    // 105 — Tamamlandı(331) hedefinde ÖNCE onaya gönderme notu sorulur. Not ZORUNLUDUR (29.09.2026) ve
    // pop-up İPTAL edilirse görev tamamlanmaz: kullanıcı kararından vazgeçebilmelidir.
    //
    // ANA GÖREVDE DE ALT GÖREVDE DE sorulur (kullanıcı kararı 2026-09-16). Başlangıçta kök görevle
    // sınırlıydı; kural, alt görevini tamamlayan kişinin "neden/nasıl bitti" bilgisini hiçbir yere
    // yazamaması anlamına geliyordu. Alan iki kademede de aynı sütuna (COMPLETION_NOTE) yazılır ve
    // uç zaten kademe ayrımı yapmıyordu — kısıt yalnız burada, istemcideydi.
    if (to === ACTION_STATUS.COMPLETED) {
      setCompletionNoteOpen(true)
      return
    }
    doChangeStatus(to)
  }

  const submitOverdueExit = () => {
    if (exitTarget == null) return
    const errorKey = overdueExitError(exitDeadline, isMainResponsible, canManageStatus, exitNote)
    if (errorKey) {
      toast.error(t(errorKey))
      return
    }
    // Not her iki rolde de doluysa gönderilir. Sorumlu → zorunlu not (Gecikti-Güncellendi); Özel Kalem →
    // isteğe bağlı not (varsa kalıcı yazılır, "temiz Güncellendi" kalır — backend ownerNoteOutcome).
    const trimmedNote = exitNote.trim()
    doChangeStatus(exitTarget, exitDeadline, trimmedNote === '' ? undefined : trimmedNote)
  }

  return {
    exitTarget,
    setExitTarget,
    exitDeadline,
    setExitDeadline,
    exitNote,
    setExitNote,
    onStatusChange,
    submitOverdueExit,
    completionNoteOpen,
    closeCompletionNote: () => setCompletionNoteOpen(false),
    /**
     * Pop-up onaylandı: notla birlikte görevi Tamamlandı'ya çek. Not boşsa **boş dize** gelir ve
     * sunucu alanı temizler — `null` gönderilseydi sunucu "alan hiç gelmedi" sayıp ESKİ notu
     * korurdu (bkz. `actionApi.changeActionStatus` yorumu).
     */
    submitCompletionNote: (completionNote: string, files: File[]) => {
      setCompletionNoteOpen(false)
      doChangeStatus(ACTION_STATUS.COMPLETED, undefined, undefined, completionNote, files)
    },
  }
}

/**
 * <b>Yan Sorumlu</b> ekle/çıkar akışı (069/072 "en az 1" kuralı + 2026-08-17 gecikme kapısı). Bileşen
 * gövdesinden bilişsel karmaşıklık gerekçesiyle çıkarıldı.
 *
 * <p>066/AK-12'nin termin pop-up'ı buradan KALDIRILDI: geciken aksiyona ekleme artık hiç yapılmıyor,
 * kullanıcı uyarılıp hedef teslim tarihini güncellemeye yönlendiriliyor.
 */
function useAssigneeActions(
  deps: PageDeps & {
    // `isOwner` KALDIRILDI: yalnız kaldırılan termin pop-up'ının rol-bazlı not zorunluluğu için vardı.
    addAssignee: ReturnType<typeof useAddAssignee>
    removeAssignee: ReturnType<typeof useRemoveAssignee>
    /** "Aksiyon Bekleniyor" statüsünün DB katalogundaki adı — uyarı metnine enterpolasyon edilir. */
    pendingStatusName: string
  },
) {
  const { data, isOverdue, addAssignee, removeAssignee, toast, t, pendingStatusName } = deps

  // Bu ekle akışı YAN Sorumlu içindir (backend de bu uçta daima 341 yazar); rol açıkça işaretlenir ki
  // iyimser gösterim ile sunucudan dönen kayıt aynı bölümde görünsün.
  const onAddAssignee = (p: PersonInput) => {
    // Ana Sorumlu aynı zamanda Yan Sorumlu olamaz (kullanıcı kuralı 2026-08-12) — uç da 400 verir.
    if (
      (data?.assignees ?? []).some(
        (x) => x.employeeId === p.employeeId && x.assigneeTypeId === ASSIGNEE_TYPE.MAIN,
      )
    ) {
      toast.error(t('actions.detail.roleConflict.secondaryIsMain'))
      return
    }
    // Geciken aksiyona Yan Sorumlu EKLENEMEZ (kullanıcı kuralı 2026-08-17) → uyarı ver, istek atma.
    // Kullanıcıya çözüm yolu da söylenir (hedef teslim tarihini güncelle). Asıl koruma backend'de:
    // aynı durum uçta 400 `overdueAssignee` döner (aşağıdaki onError o kodu da karşılar).
    //
    // ÖNCESİ (066/AK-12): ekleme yapılabiliyordu ama karşılığında yeni termin + gerekçe notu isteyen bir
    // pop-up açılıyordu; sorumlu ekleme akışı sessizce bir termin güncelleme akışına dönüşüyordu. O
    // pop-up ve ona bağlı üç yerel durum (bekleyen kişi / termin / not) bu turda kaldırıldı.
    // Kapı yalnız "Aksiyon Bekleniyor" statüsünde konuşur: başka statüde gerçek ret sebebi 085/FR-008'in
    // yapı kilidi (`actionNotPending`) ve o mesajı uçtan gelen hata eşleyicisi gösterir. Ekranın `isOverdue`
    // ölçütü statüye bakmaz (yalnız termin), o yüzden statü koşulu burada açıkça yazılır — aksi halde
    // Tamamlandı + termini geçmiş kayıtta kullanıcı yanlış gerekçe okuyordu (code-review bulgusu).
    //
    // BU DALIN KENDİ FRONTEND TESTİ YOK ve bu bilinçlidir (bağımsız code-review bulgusu 2026-08-17):
    // kuralın otoritesi backend'de ve orada test edilmiş; mesajın doğru metne bağlandığını
    // `statusError.test.ts` sabitliyor. Dal silinse kullanıcı AYNI uyarıyı görür (istek uca gider, 400
    // `overdueAssignee` döner, eşleyici aynı anahtarı verir) — kaybedilen tek şey gereksiz bir istektir.
    // Bunu test etmek PrimeReact AutoComplete overlay'ini sürmeyi gerektiriyor; depoda böyle bir emsal
    // yok ve kırılgan olurdu. Bedel bilerek kabul edildi.
    if (isOverdue && data?.statusId === ACTION_STATUS.PENDING) {
      toast.error(t('actions.detail.assignees.overdueBlocked'))
      return
    }
    addAssignee.mutate(
      { person: p },
      {
        onSuccess: () => toast.success(t('actions.detail.assignees.added')),
        onError: (err) => toast.error(t(assigneeAddErrorKey(err), { status: pendingStatusName })),
      },
    )
  }

  const onRemoveAssignee = (employeeId: string) => {
    // Ana Sorumlu zorunludur → çıkarılamaz (değiştirmek için Ana Sorumlu seçicisi kullanılır). Backend de 400 verir.
    const target = (data?.assignees ?? []).find((x) => x.employeeId === employeeId)
    if (target && target.assigneeTypeId === ASSIGNEE_TYPE.MAIN) {
      toast.error(t('actions.detail.mainResponsible.cannotRemove'))
      return
    }
    // 069/072 — Sorumlu min 1: son sorumlu çıkarılamaz (aksiyon sorumlusuz kalmaz). Backend de doğrular.
    if ((data?.assignees?.length ?? 0) <= 1) {
      toast.error(t('actions.detail.assignees.minOne'))
      return
    }
    removeAssignee.mutate(employeeId, {
      onSuccess: () => toast.success(t('actions.detail.assignees.removed')),
      onError: (err) => toast.error(t(assigneeRemoveErrorKey(err), { status: pendingStatusName })),
    })
  }

  return { onAddAssignee, onRemoveAssignee }
}

/**
 * "Geri" hedefi: origin varsa (useNavigateWithOrigin ile taşınan GELDİĞİ konum) doğrudan oraya döner —
 * anasayfa, filtreli aksiyon listesi veya toplantı detayı fark etmez (search dahil korunur). Origin yoksa
 * (tam sayfa yenileme / derin link) makul fallback'e düşer: arşiv rotasında arşiv listesi, toplantıya bağlı
 * aksiyonda toplantı detayı, aksi halde aksiyon listesi.
 */
function deriveBackTo(
  pathname: string,
  state: unknown,
  meetingId: number | null | undefined,
): string {
  const fromState = (state ?? null) as { from?: string } | null
  if (fromState?.from) return fromState.from
  if (pathname.startsWith('/minutes/archived/actions')) return '/minutes/archived/actions'
  if (meetingId != null) return `/minutes/meeting-minutes/meetings/${meetingId}`
  return '/minutes/actions'
}

/**
 * "Geri" butonunun etiket anahtarı — hedefin GERÇEK kategorisinden türetilir (etiket ile gidilen yer
 * çelişmesin): toplantı DETAYI (/minutes/meeting-minutes/meetings/:id) → "Toplantı Detayına Dön"; aksiyon listesi
 * (aktif/arşiv) → "Aksiyon Listesine Dön"; bunların dışı (ör. anasayfa kartı origin'i "/") → nötr "Geri".
 * (Önce regex dışı HER hedef "liste" sayılıyordu → anasayfadan gelince buton "Aksiyon Listesine Dön" deyip
 * Anasayfa'ya gidiyordu.)
 */
function backLabelKey(backTo: string): string {
  // Toplantı detayı İKİ önekte yaşar: aktif kayıt 2026-09-16'da "Toplantı Tutanakları" grubunun
  // altına taşındı (`/minutes/meeting-minutes/meetings/:id`), arşiv kaydı yerinde kaldı
  // (`/minutes/archived/meetings/:id`). Desen ikisini de tanımazsa buton toplantıya gider ama
  // üzerinde nötr "Geri" yazar — etiket ile hedef çelişir (bu javadoc'un önlemeye çalıştığı hata).
  if (/^\/minutes\/(meeting-minutes|archived)\/meetings\/\d+/.test(backTo))
    return 'actions.detail.backToMeeting'
  if (/^\/minutes\/(archived\/)?actions/.test(backTo)) return 'actions.detail.backToList'
  return 'actions.detail.back'
}

/**
 * Termin alt sınırları. minDeadline her durumda ≥ bugün (bağımsız aksiyonda da) — geçmiş termin seçilemez;
 * toplantıya bağlıysa ayrıca gelecekteki toplantı tarihinden önce olamaz (deadlineMinDate = max(bugün,
 * toplantı)). parentDeadlineMin ise ana aksiyon termininin alt sınırıdır: devam eden alt aksiyonların en
 * büyük termininden önce olamaz (alt ≤ ana; toplantı bağımlıda yalnız manuel olanlar — bkz.
 * openSubActionsMaxDeadline). Yalnız inline ANA termin alanı için; yeni alt aksiyon
 * (SubActionDialog) minDeadline'ı base kalır (etkilenmez).
 */
function deriveDeadlineBounds(data: ActionDetail | undefined) {
  const minDeadline = deadlineMinDate(data?.meetingDate?.slice(0, 10))
  const openSubMax = openSubActionsMaxDeadline(data?.subActions, data?.meetingId != null)
  const parentDeadlineMin = openSubMax && openSubMax > minDeadline ? openSubMax : minDeadline
  return { minDeadline, parentDeadlineMin }
}

/**
 * **Aksiyon Bekleniyor (330) iken açılır liste yerine tek bir "Onaya Gönder" düğmesi mi?** (kullanıcı
 * isteği 29.09.2026). O statüde matristeki tek hedef zaten 331'dir
 * ({@code MeetingActionStatus.MAIN_TRANSITIONS}), yani tek seçenekli bir açılır liste kullanıcıya
 * gereksiz bir adım saydırıyordu.
 *
 * <p>Koşul hedefin **gerçekten izinli olmasına** bakar, statüye değil: yetki/kapsam süzgeçleri
 * ({@code allowedTargets}) 331'i elerse düğme de çizilmez — aksi hâlde basınca 403 alan bir düğme
 * kalırdı. Diğer tüm statülerde açılır liste aynen durur.
 *
 * <p><b>Neden bileşenin DIŞINDA:</b> ölçüldü (29.09.2026, `eslint-plugin-sonarjs`) — hesap gövdeye
 * yazıldığında `ActionDetailPage`in bilişsel karmaşıklığı **15 → 16** çıkıyor ve Sonar S3776 eşiğini
 * (15) aşıyordu. Dosyanın üstündeki "türetilen yetkiler bileşenin dışında" kuralı tam bu yüzden var.
 */
function isSendForApprovalOnly(statusId: number | null | undefined, baseTargets: number[]): boolean {
  return (
    statusId === ACTION_STATUS.PENDING &&
    baseTargets.length === 1 &&
    baseTargets[0] === ACTION_STATUS.COMPLETED
  )
}

/**
 * Aksiyon Detay sayfası (analiz 7). Termin kartı (statü rengi) + Aksiyon Bilgileri (toplantı/no/termin/önem/
 * Sorumlu/takip edenler) + Aksiyon Açıklaması (tanım+detay, Güncelle) + Alt Aksiyonlar (oluştur/listele)
 * + Aksiyon Notları (yalnız metin; termin değişince zorunlu) + Dokümanlar (ekle/indir; ACTION_FILE) + Statü
 * yönetimi (geçiş kuralları). <b>Sorumlu</b> çoklu (sicil dedup). <b>Aksiyon Geçmişi</b> görünür-pasif. Yalnız yetkili
 * (MINUTES_CREATE) düzenler; Tamamlandı/İptal salt-okuma.
 *
 * <p>Popup akışları ve türetilen yetkiler bileşenin DIŞINDA (yukarıdaki hook'lar/saf yardımcılar): gövdenin
 * bilişsel karmaşıklığını Sonar S3776 eşiğinin altında tutmak için.
 */
export default function ActionDetailPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const { hasGrant, hasAnyGrant } = useGrants()
  const { readOnly } = useProxyMode()
  // 077 — Alt Görev No kolonu yalnız RECORD_NUMBER_VIEW yetkisinde (Özel Kalem).
  const showRecordNumber = hasGrant(GRANTS.RECORD_NUMBER_VIEW)
  // Alt görevler tablosunun dar ekran karşılığı (kullanıcı bildirimi 2026-09-09) — bkz. SubActionCards.
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  const { data: me } = useCurrentUser()

  const { id } = useParams()
  const navigate = useNavigate()
  /**
   * Alt göreve geçişte GELDİĞİ konumu taşır (2026-09-10, kullanıcı bildirimi: *"görev detayından
   * alt göreve geçince Geri'ye basınca Oluşturduklarım'a atıyor, görev detayına dönsün"*).
   *
   * Alt Görev Detay'ın "Geri"si önce `location.state.from`'a bakar; buradan düz `navigate` ile
   * gidildiği için state hiç yazılmıyordu ve sayfa kendi yedek yoluna düşüyordu. Uygulamadaki
   * diğer tüm görev geçişleri (liste, panolar, tutanak editörü) zaten bu kancayı kullanıyor —
   * eksik olan tek yer burasıydı.
   */
  const navigateWithOrigin = useNavigateWithOrigin()
  const location = useLocation()
  const actionId = id ? Number(id) : null
  const aid = actionId ?? 0
  const { data, isError } = useActionDetail(actionId)

  /**
   * Görev **üzerinde işlem yapılabilir mi** — kararı SUNUCU verir (`editable`, 2026-08-26).
   *
   * Ölçüt kayıt düzeyindedir ve iki kural taşır: yalnız **takip eden** kullanıcı hiçbir işlem yapamaz,
   * sadece görüntüler (2026-08-26); **Yan Sorumlu (Süreç Destek Yöneticisi) ana görevi güncelleyemez**
   * (2026-08-28) — işini kendi otomatik **alt görevinde** yapar, orada bayrak yine `true` gelir. İkisi de
   * grant'lardan türetilemez: Yönetici rolü `ACTION_MANAGE` taşır ve eskiden takip ettiği ya da yalnız yan
   * sorumlusu olduğu bir görevin alanlarını/sorumlularını/dosyalarını değiştirebiliyordu.
   *
   * Bayrak üç yetki bayrağının **hepsine** AND'lenir, tek tek yüzeylere değil: aksi hâlde yeni bir
   * düzenleme yüzeyi eklendiğinde onu buraya bağlamayı unutmak sessiz bir delik açardı.
   * Veri gelmeden **fail-closed** (`?? false`): kontroller kısa süre görünüp kaybolmaz.
   */
  const serverEditable = data?.editable ?? false
  // Alan/içerik düzenleme = Tutanak Oluştur (Özel Kalem).
  const canManage = hasGrant(GRANTS.ACTION_MANAGE) && serverEditable
  // Görev STATÜ yönetimi (Özel Kalem yolu): Tutanak Oluştur VEYA atanabilir Görev Durum Güncelleme.
  const canManageStatus =
    hasAnyGrant(GRANTS.MINUTES_CREATE, GRANTS.ACTION_STATUS_UPDATE) && serverEditable
  // Görev notu Düzenle/Sil (herhangi bir notu) — ayrı yetki (ekleme ACTION_MANAGE ile).
  const canManageNotes = hasGrant(GRANTS.ACTION_NOTE_MANAGE) && serverEditable
  /**
   * Kayıt silme (2026-09-11) — görev + alt görev. `serverEditable` ŞARTI ARANMAZ: o bayrak tutanağın
   * düzenlenebilir olup olmadığını söyler (taslak/yayımlanmış, arşiv), silme ise ayrıcalıklı bir işlemdir
   * ve yayımlanmış bir tutanağın görevini de silebilmelidir. Sunucu yine son sözü söyler (403).
   */
  const canDeleteRecord = hasGrant(GRANTS.RECORD_DELETE)

  /**
   * Görüntüleme denetim izi (kod 536) — aksiyon başına **bir kez**.
   *
   * <p>`ref` muhafızı ZORUNLU: React geliştirme modunda (StrictMode) effect'ler iki kez çağrılır ve bu
   * effect idempotent değildir — her çağrı MEETING_LOG'a satır yazar. Muhafız olmadan tek ekran açılışı
   * iki iz üretir (kod 500'de canlı denemede görüldü). Üretimde çift çağrı olmaz ama kural "bir
   * kullanıcı eylemi = bir satır"dır (093/FR-013) ve geliştirme ortamında da geçerli olmalı.
   *
   * <p>Bağımlılık `actionId`: aynı ekranda başka bir aksiyona geçilirse (rota parametresi değişir) yeni
   * bir görüntüleme olayıdır ve yazılmalıdır — bu yüzden muhafız da aksiyon kimliğini tutar.
   *
   * <p>`await` edilmez ve hata yutulur: iz, ekranın açılmasını engellememelidir.
   */
  const viewLoggedFor = useRef<number | null>(null)
  useEffect(() => {
    if (actionId == null || viewLoggedFor.current === actionId) return
    viewLoggedFor.current = actionId
    recordActionViewAccess(actionId)
  }, [actionId])

  const {
    statusId,
    isSubAction,
    isMainResponsible,
    isOverdue,
    editable,
    canChangeStatus,
    fieldsEditable,
    deadlineEditable,
    infoEditable,
    structurallyEditable,
    deadlineRequestAllowed,
  } = derivePermissions(data, me?.employeeId, canManage, canManageStatus, readOnly)

  /**
   * Alt görev satırında çöp kutusu çizilir mi? Koşul JSX'ten ÇIKARILDI: bileşen gövdesinin bilişsel
   * karmaşıklığı zaten Sonar eşiğinin (15) tam sınırındaydı ve iki koşul onu 16'ya taşımıştı (push
   * kapısı ölçümü). Kural aynı: kolon düzenlenebilir bağlamda ya da silme yetkisiyle görünür; satır
   * düğmesi manuel kayıtlarda herkese, otomatiklerde yalnız yetkiliye.
   */
  const subDeleteColumnVisible = editable || canDeleteRecord
  const subDeleteVisible = (s: SubAction) => s.manual || canDeleteRecord

  /**
   * İÇERİK değişikliği (not düzenle/sil, dosya ekle/kaldır) SONLANMIŞ statüde kapalıdır — Kapatıldı /
   * Arşivlendi (kullanıcı kararı 2026-08-26). Tamamlandı dışarıdadır: o statüden geri dönülebiliyor ve
   * alan düzenleme de açık kalıyor (bkz. `isTerminalStatus`).
   *
   * <p>Önceki hâlde bu iki yüzey YALNIZ yetkiye bakıyordu (`canManage` / `canManageNotes`) — statüye
   * hiç bakmıyordu, dolayısıyla kapatılmış bir görevde dosya eklenip not silinebiliyordu. Sunucu da
   * kontrol etmiyordu; uç tarafı da bu turda kapatıldı (`requireNotTerminalForContentChange`).
   */
  const terminal = isTerminalStatus(statusId)
  const notesManageable = canManageNotes && !terminal
  const documentsEditable = canManage && !terminal

  const update = useUpdateAction(aid)
  const changeStatus = useChangeActionStatus(aid)
  // Onaya gönderme pop-up'ının dosya alanı için; Dokümanlar bölümü kendi örneğini ayrıca kurar
  // (aynı uç, aynı tazeleme anahtarı — iki örneğin bekleyen durumu birbirine karışmaz).
  const uploadAttachments = useUploadAttachments(aid)
  const addAssignee = useAddAssignee(aid)
  const removeAssignee = useRemoveAssignee(aid)
  const setMainResponsibleMut = useSetMainResponsible(aid)
  const createSub = useCreateSubAction(aid)
  const deleteSub = useDeleteSubAction(aid)
  const updateNoteMut = useUpdateActionNote(aid)
  const deleteNoteMut = useDeleteActionNote(aid)
  const transitions = useAllowedTransitions(actionId, statusId)
  // Statü ADI tek kaynaktan: DB katalogu (i18n yalnız yedek) — bkz. useActionStatusName.
  const statusName = useActionStatusName()
  const pendingStatusName = statusName(ACTION_STATUS.PENDING)

  // Ekranda YALNIZ iki bölüm vardır: Ana Sorumlu (tekil) ve Yan Sorumlu (0..n) — kullanıcı kuralı
  // 2026-08-12. 2026-08-12 öncesi ROLSÜZ sorumlular gösterilmez; kayıtları DB'de durur ve tutanak
  // senkronunda kayıpsız taşınır (bkz. buildActionPayload), yalnız bu ekranda listelenmezler.
  const mainResponsible = mainResponsibleOf(data?.assignees)
  const secondaryResponsibles = secondaryResponsiblesOf(data?.assignees)

  // Yan Sorumlu olan biri Ana Sorumlu seçilemez (kullanıcı kuralı 2026-08-12): sistem kişiyi Yan'dan
  // alıp Ana'ya TAŞIMAZ. Ekran isteği hiç göndermeden reddeder; uç da aynı kuralı zorlar (asıl koruma).
  const onSetMainResponsible = (p: PersonInput) => {
    if (secondaryResponsibles.some((x) => x.employeeId === p.employeeId)) {
      toast.error(t('actions.detail.roleConflict.mainIsSecondary'))
      return
    }
    setMainResponsibleMut.mutate(
      { ...p, assigneeTypeId: ASSIGNEE_TYPE.MAIN },
      {
        onSuccess: () => toast.success(t('actions.detail.mainResponsible.set')),
        onError: (err) => toast.error(t(mainResponsibleSetErrorKey(err))),
      },
    )
  }

  // Alt görev sil (066/7.16) — onay iste (yıkıcı işlem, Anayasa).
  const confirmSubDelete = (subId: number) =>
    confirmDialog({
      group: 'subaction-delete',
      header: t('actions.detail.subActions.delete'),
      message: t('actions.detail.subActions.deleteConfirm'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      accept: () =>
        deleteSub.mutate(subId, {
          onSuccess: () => toast.success(t('actions.detail.subActions.deleted')),
          // Otomatik alt görevde silme, üst görevi sorumlusuz bırakacaksa 400 döner; jenerik "Alt görev
          // silinemedi." kullanıcıya ne yapacağını söylemiyordu (2026-09-17 — eşleme liste ekranıyla
          // ORTAK: aynı başarısızlık iki ekranda farklı anlatılmasın).
          onError: (err) => toast.error(t(subActionDeleteErrorKey(err))),
        }),
    })

  // Düzenlenebilir alanlar (tanım/açıklama/termin) — detaydan başlatılır; Aksiyon Güncelle ile kaydedilir.
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [deadline, setDeadline] = useState('')
  // Stratejik Hedef / Katkı (127) — kimlik alanlarıyla aynı kuralda düzenlenir, Görev Güncelle ile kaydedilir.
  const [classification, setClassification] = useState<TaskClassification>(EMPTY_CLASSIFICATION)
  // Aksiyon Notu (yalnız metin) — termin değişince zorunlu (7.8); güncellemeyle kaydedilir.
  const [note, setNote] = useState('')
  const [noteDialogOpen, setNoteDialogOpen] = useState(false)
  // Not düzenleme (ACTION_NOTE_MANAGE) — seçili not + taslak metin; null ise dialog kapalı.
  const [editNote, setEditNote] = useState<ActionNote | null>(null)
  const [editNoteText, setEditNoteText] = useState('')
  const [subOpen, setSubOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false) // 074 — İşlem Geçmişi dialog'u
  const [deadlineRequestOpen, setDeadlineRequestOpen] = useState(false) // 087 — termin talebi pop-up'ı
  // Ana/kök aksiyon İptal edilirken zorunlu gerekçe (not) popup'ta alınır; alt aksiyonda not yok (AK-7).

  // 066/AK-12 — iki popup akışı (Gecikti'den çıkış · Gecikti aksiyona sorumlu ekleme) kendi hook'larında;
  // ikisi de yeni termin toplar. Dönen adlar eski yerel adlarla aynı olduğundan aşağıdaki JSX aynen çalışır.
  const {
    exitTarget,
    setExitTarget,
    exitDeadline,
    setExitDeadline,
    exitNote,
    setExitNote,
    onStatusChange,
    submitOverdueExit,
    completionNoteOpen,
    closeCompletionNote,
    submitCompletionNote,
  } = useOverdueExit({
    data,
    isOverdue,
    isMainResponsible,
    canManageStatus,
    changeStatus,
    uploadAttachments,
    toast,
    t,
  })
  const { onAddAssignee, onRemoveAssignee } = useAssigneeActions({
    data,
    isOverdue,
    addAssignee,
    removeAssignee,
    toast,
    t,
    pendingStatusName,
  })

  // 085/FR-003 — İptal statüsü kaldırıldı; zorunlu gerekçeli İptal popup'ı ve ona bağlı kademeli
  // alt-aksiyon uyarısı da kalktı. Statü değişimi doğrudan hook'a gider.
  const handleStatusChange = (to: number) => onStatusChange(to)

  // 087 — Termin güncelleme talebi. Buton görünürlüğü SUNUCUDAN gelir (data.deadlineRequestAllowed);
  // burada hiçbir uygunluk kuralı yeniden hesaplanmaz.
  const createDeadlineRequest = useCreateDeadlineRequest(actionId)
  const submitDeadlineRequest = (input: { requestedDate: string; requestNote: string }) => {
    if (actionId == null) return
    createDeadlineRequest.mutate(
      { actionId, ...input },
      {
        onSuccess: () => {
          setDeadlineRequestOpen(false)
          toast.show({
            severity: 'success',
            summary: t('deadlineRequests.toast.created'),
            life: 3000,
          })
        },
      },
    )
  }

  useEffect(() => {
    if (data) {
      setName(data.name ?? '')
      setDescription(data.description ?? '')
      setDeadline(data.deadline ?? '')
      setClassification(classificationOf(data))
    }
  }, [data])

  useSetBreadcrumb(breadcrumbSteps(data, t('actions.detail.title')))

  if (isError) {
    return (
      <div className="page minutes-page">
        <PageHeader title={t('actions.detail.title')} />
        <EmptyState
          icon="pi pi-exclamation-triangle"
          title={t('actions.detail.notFound.title')}
          description={t('actions.detail.notFound.desc')}
        />
      </div>
    )
  }

  const meetingBound = data?.meetingId != null
  // Aciliyet/Önem Derecesi adları sunucuda REFERANS'tan çözülür (statü adıyla aynı kalıp; ad değişince yansır).
  const urgencyName = data?.urgencyLevelName ?? '—'
  const importanceName = data?.importanceLevelName ?? '—'
  // Termin alt sınırları (gerekçeler deriveDeadlineBounds javadoc'unda).
  const { minDeadline, parentDeadlineMin } = deriveDeadlineBounds(data)
  // Gecikti→Güncellendi'de yeni termin "bugün veya sonrası" olmalı (her iki rol) → takvim alt sınırı bugün.
  const overdueExitMinDate = todayStart()

  // Statü DEĞİŞTİRME listesi yalnız TEMEL etiket gösterir (Devam Eden/Güncellendi/Tamamlandı/İptal); gecikmeli
  // çeşitler (332/336/337) burada görünmez. Mevcut statü + izinli hedefler TEMEL statüye indirilip tekrarsız
  // yapılır; concrete gecikmeli çeşidi backend IS_DELAYED_UPDATED'e göre seçer. Etiket id'ye bağlı değil (elle).
  const baseTargets = Array.from(
    new Set(
      visibleStatusTargets(statusId, transitions.data)
        .map((sid) => baseStatusOf(sid))
        .filter((sid): sid is number => sid != null),
    ),
  )
  // Etiketler DB katalogundan gelir (i18n yalnız katalog yüklenmeden önceki yedek): paket ile katalog
  // ayrışınca dropdown var olmayan bir statü adı gösteriyordu (kullanıcı bulgusu 2026-08-27).
  const statusOptions = baseTargets.map((sid) => ({
    value: sid,
    label: statusName(sid),
  }))

  const sendForApprovalOnly = isSendForApprovalOnly(statusId, baseTargets)

  // Termin değişti mi? (7.8: termin güncellenirken not zorunlu.)
  const deadlineChanged = data != null && deadline !== (data.deadline ?? '')

  // Asıl kayıt: alan güncellemesi + (varsa) not TEK işlemde (analiz 7.10). Not popup'ının Kaydet'i ve termin
  // değişmediğinde Aksiyon Güncelle bunu doğrudan çağırır. Not yalnız metindir (dosya eki yok).
  const submitUpdate = () => {
    if (!data) return
    const noteText = note.trim()
    const errorKey = updateError(name, description, deadline, deadlineChanged, noteText)
    if (errorKey) {
      toast.error(t(errorKey))
      return
    }
    update.mutate(
      {
        name: name.trim(),
        description: description.trim(),
        deadline,
        note: noteText === '' ? undefined : noteText,
        modDate: data.modDate,
        // 127 — seçim her zaman tam liste gider; sunucu değişmediyse yazmaz ve kapı uygulamaz.
        ...classificationIds(classification),
      },
      {
        onSuccess: () => {
          toast.success(t('actions.detail.update.success'))
          setNote('')
          setNoteDialogOpen(false)
        },
        onError: () => toast.error(t('actions.detail.update.error')),
      },
    )
  }

  // Aksiyon Güncelle: termin değişip not boşsa notu popup'ta al (zorunlu), aksi halde doğrudan kaydet.
  const onSaveFields = () => {
    if (deadlineChanged && note.trim() === '') {
      setNoteDialogOpen(true)
      return
    }
    submitUpdate()
  }

  // Not düzenle (ACTION_NOTE_MANAGE) — seçili notu taslağa al, popup aç.
  const openEditNote = (n: ActionNote) => {
    setEditNote(n)
    setEditNoteText(n.note)
  }
  // Kaydet: yalnız metin; boş → engelle. Hata (409 bayat modDate / 404 / vb.) merkezî bildirime düşer.
  const submitEditNote = () => {
    if (!editNote) return
    const text = editNoteText.trim()
    if (text === '') {
      toast.error(t('actions.detail.notes.required'))
      return
    }
    updateNoteMut.mutate(
      { noteId: editNote.id, note: text, modDate: editNote.modDate },
      {
        onSuccess: () => {
          toast.success(t('actions.detail.notes.updated'))
          setEditNote(null)
        },
        // Bayat ekran/iki sekme: statü bu arada Kapatıldı'ya geçmiş olabilir → sunucu reddeder.
        // Jenerik mesaj nedeni söylemez, o yüzden gerekçe gösterilir.
        onError: (e) => toast.error(t(noteMutationErrorKey(e, 'actions.detail.notes.updateError'))),
      },
    )
  }
  // Sil (soft-delete) — yıkıcı işlem, onay iste (Anayasa). Başarı toast'ı; hata merkezî bildirimde.
  const confirmNoteDelete = (n: ActionNote) =>
    confirmDialog({
      group: 'note-delete',
      header: t('actions.detail.notes.delete'),
      message: t('actions.detail.notes.deleteConfirm'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      accept: () =>
        deleteNoteMut.mutate(n.id, {
          onSuccess: () => toast.success(t('actions.detail.notes.deleted')),
          onError: (e) => toast.error(t(noteMutationErrorKey(e, 'actions.detail.notes.deleteError'))),
        }),
    })

  // "Geri" hedefi ve etiketi (gerekçeler deriveBackTo / backLabelKey javadoc'larında).
  const backTo = deriveBackTo(location.pathname, location.state, data?.meetingId)
  const backLabel = t(backLabelKey(backTo))
  // Origin (useNavigateWithOrigin ile taşınan `from`) varsa bu sayfaya GERÇEKTEN o konumdan push edilerek
  // gelinmiştir → "Geri" tarayıcı geçmişinden POP etmeli (navigate(-1)), yeniden push ETMEMELİ. Push, her
  // ziyarette history yığınının üstüne aynı listeyi tekrar tekrar biriktiriyordu (kullanıcı bildirimi).
  // Origin yoksa (tam sayfa yenileme/derin link) geçmişte dönülecek ilgili bir kayıt yoktur → push'a düşülür.
  const hasOrigin = Boolean((location.state as { from?: string } | null)?.from)

  // Alt aksiyon (parentId dolu) bu ekranda gösterilmez → ayrı Alt Aksiyon Detay sayfasına yönlendir (066).
  // (Eski linkler / "Bana Atananlar" listesinden gelen alt aksiyonlar için güvenlik ağı.)
  if (data && data.parentId != null) {
    return <Navigate to={`/minutes/subactions/${aid}`} replace state={location.state} />
  }

  return (
    <div className="page minutes-page">
      <div className="mtg-detail">
        {/* Üst bar (hepsi sağda): Geri (listeye/toplantı detayına) · Aksiyon Geçmişi (görünür-pasif) · statü. */}
        <div className="act-detail-topbar">
          <Button
            variant="secondary"
            size="small"
            icon="pi pi-arrow-left"
            label={backLabel}
            onClick={() => (hasOrigin ? navigate(-1) : navigate(backTo))}
          />
          <Button
            variant="secondary"
            size="small"
            icon="pi pi-history"
            label={t('actions.detail.history')}
            onClick={() => setHistoryOpen(true)}
          />
          {/* Görevi Sil düğmesi BURADA DEĞİL (kullanıcı kararı 2026-09-11): silme, Toplantı Detay'daki
              Görev tablosunda Düzenle'nin yanındaki çöp kutusundan yapılır. Detay sayfasının üst barı
              gezinme/geçmiş/statü içindir; yıkıcı işlem listenin bağlamında daha okunaklı duruyor.
              Alt görev silme bu sayfada KALIR — onun listesi de burada. */}
          {/* Statü değiştirme: yönetim VEYA alt aksiyon sahibi + geçişi olan aksiyonda; salt-okumada gösterilmez
              — güncel durum "Aksiyon Bilgileri > Durum" alanında görünür (taslak toplantıda da orada). */}
          {canChangeStatus && sendForApprovalOnly && (
            <Button
              size="small"
              icon="pi pi-send"
              label={t('actions.detail.status.sendForApproval')}
              onClick={() => handleStatusChange(ACTION_STATUS.COMPLETED)}
              disabled={changeStatus.isPending}
            />
          )}
          {canChangeStatus && !sendForApprovalOnly && statusOptions.length > 0 && (
            <Dropdown
              value={null}
              options={statusOptions}
              optionLabel="label"
              optionValue="value"
              placeholder={t('actions.detail.status.change')}
              onChange={(e) => handleStatusChange(e.value as number)}
              className="act-status-select"
              disabled={changeStatus.isPending}
            />
          )}
        </div>

        {/* Termin Bilgi Kartı (7.5) + 087 termin güncelleme talebi butonu. */}
        <ActionTerminCard
          deadline={data?.deadline ?? null}
          statusId={statusId}
          requestAllowed={deadlineRequestAllowed}
          pendingRequestId={data?.pendingDeadlineRequestId ?? null}
          onRequestDeadlineUpdate={() => setDeadlineRequestOpen(true)}
        />

        {/* Form da kipe bağlı — `AgendaFormDialog` ile aynı gerekçe: düğmeyi gizlemek yetmez,
            `deadlineRequestOpen` başka bir yoldan dolarsa pop-up yine açılırdı. Bugün erişilemez
            (yalnız kapatılan düğme açıyor), ama gerekçe simetrik uygulanıyor. */}
        {!readOnly && (
          <DeadlineRequestDialog
            visible={deadlineRequestOpen}
            currentDeadline={data?.deadline ?? null}
            parentDeadline={data?.parentDeadline ?? null}
            submitting={createDeadlineRequest.isPending}
            onSubmit={submitDeadlineRequest}
            onHide={() => setDeadlineRequestOpen(false)}
          />
        )}

        {/* Aksiyon Bilgileri (7.6). */}
        <section className="minutes-section">
          <div className="minutes-section-head">
            <h2>{t('actions.detail.info.title')}</h2>
            {/*
              "Gecikti" — katalogda BÖYLE BİR STATÜ YOKTUR (085/FR-012: gecikme artık statüden değil
              `IS_DELAYED_UPDATED` bayrağından türetilir); bu yalnızca kırmızı bir işarettir. Bayrağı
              sunucu değerlendirir (`overdueLabel`); 2026-08-17'den beri koşul YALNIZ BAYRAKTIR — kayıt
              Tamamlandı/Kapatıldı/Arşivlendi'ye geçse de işaret durur ("zamanında bitmedi" tarihsel bir
              olgudur). İstemci kuralı yeniden hesaplamaz. Başlığın sağında durur (kullanıcı isteği
              2026-08-14): statü hücresinin yanındaki eski konumdan daha görünür.
            */}
            {data?.overdueLabel && (
              <span className="act-overdue-label">{t('actions.status.overdueLabel')}</span>
            )}
          </div>
          {/* Sıra: Aksiyon Numarası (en başta) · Toplantı Adı · Yer · Zaman · Termin Tarihi · Önem Derecesi.
              Toplantı alanları yalnız toplantıya bağlı aksiyonda gösterilir. */}
          <ActionInfoGrid
            data={data}
            meetingBound={meetingBound}
            urgencyName={urgencyName}
            importanceName={importanceName}
            statusId={statusId}
          />

          {/* Ana Sorumlu (2026-08-12) — TEKİL ve zorunlu. Seçici açık kalır (disableWhenSet yok): yeni kişi
              seçilince eskisinin yerini alır; "temizle" yoktur (canClear=false) çünkü alan boş bırakılamaz.
              Alt aksiyonda bu bölüm gösterilmez — orada atanan kendi sayfasından değişir (AK-5/073). */}
          {!isSubAction && (
            <div className="minutes-subsection">
              <h3 className="minutes-subhead">
                {t('actions.detail.mainResponsible.title')}
                {infoEditable && <span className="ui-req"> *</span>}
              </h3>
              <SinglePersonEditor
                person={mainResponsible}
                editable={infoEditable}
                canClear={false}
                onSet={onSetMainResponsible}
                onClear={() => {}}
                placeholder={t('actions.detail.mainResponsible.placeholder')}
                emptyText={t('actions.detail.mainResponsible.empty')}
                removeLabel={t('actions.detail.assignees.remove')}
              />
            </div>
          )}

          {/* Yan Sorumlu (2026-08-12) — çoklu ve OPSİYONEL; her biri için otomatik alt aksiyon üretilir. */}
          {!isSubAction && (
            <div className="minutes-subsection">
              <h3 className="minutes-subhead">{t('actions.detail.secondaryResponsibles.title')}</h3>
              <ActionPeopleEditor
                people={secondaryResponsibles}
                editable={infoEditable}
                onAdd={onAddAssignee}
                onRemove={onRemoveAssignee}
                placeholder={t('actions.detail.secondaryResponsibles.placeholder')}
                emptyText={t('actions.detail.secondaryResponsibles.empty')}
                removeLabel={t('actions.detail.assignees.remove')}
              />
            </div>
          )}

          {/* Takip Edenler (7.15) — SALT-OKUMA (120, kullanıcı isteği 2026-10-07): kişi arama/ekleme ve
              çıkarma yok. Kişi kendini görev listelerindeki / Toplantı Detay'daki yıldızla takibe alır;
              başkasını takipçi yapan uç da kaldırıldı. Eski kayıtlar burada görünmeye devam eder. */}
          <div className="minutes-subsection">
            <h3 className="minutes-subhead">{t('actions.detail.followers.title')}</h3>
            <ActionPeopleEditor
              people={data?.followers ?? []}
              editable={false}
              onAdd={noop}
              onRemove={noop}
              placeholder=""
              emptyText={t('actions.detail.followers.empty')}
              removeLabel=""
            />
          </div>
        </section>

        {/* Tamamlanma Notu — bilgi kartının hemen altında, Açıklama'nın ÜSTÜNDE. Bileşen Alt Görev
            Detay ve "Ana Görevi Gör" ile ORTAKTIR; not yoksa hiçbir şey çizilmez. */}
        <ActionCompletionNote note={data?.completionNote} />

        {/* Aksiyon Açıklaması (7.7): Tanım + Detay. */}
        <section className="minutes-section">
          <div className="minutes-section-head">
            <h2>{t('actions.detail.desc.title')}</h2>
          </div>
          <div className="act-desc-grid">
            <label className="act-field">
              <span className="ui-field-label">
                {t('actions.form.name')} {infoEditable && <span className="ui-req">*</span>}
              </span>
              <InputTextarea
                value={name}
                rows={3}
                autoResize
                maxLength={250}
                disabled={!infoEditable}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="act-field">
              <span className="ui-field-label">
                {t('actions.detail.desc.detail')}{' '}
                {infoEditable && <span className="ui-req">*</span>}
              </span>
              <InputTextarea
                value={description}
                rows={3}
                autoResize
                maxLength={2000}
                disabled={!infoEditable}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
          </div>
          {fieldsEditable && (
            <label className="act-field act-field--inline act-desc-deadline">
              <span className="ui-field-label">
                {t('actions.form.deadline')} <span className="ui-req">*</span>
              </span>
              {/*
                Termin GECİKMİŞ aksiyonda da buradan değiştirilebilir (kullanıcı kuralı 2026-08-17):
                bu alan yalnız `fieldsEditable` iken render edilir. Yetkisi olmayan sorumlu alanı hiç
                görmez; onun yolu 087 "Hedef Teslim Tarihi Güncelleme Talebi"dir.

                KURAL DEĞİŞTİ 2026-11-02 (130, kullanıcı): tarihi yalnız `ACTION_DEADLINE_UPDATE` yetkisi
                taşıyan ya da görevi OLUŞTURAN değiştirir; alan diğerlerine salt-okuma görünür
                (`deadlineEditable`, sunucudan). Atananın yolu 087 talebidir. Önceki kurallar: 2026-10-02
                "yönetim ∨ oluşturan ∨ atanan değil", 2026-08-31 "yazma hakkı olan herkes". Sunucu da
                aynı kuralı zorlar (`ActionDeadlinePolicy#requireIfChanged`).

                ÖNCESİ: `disabled={isOverdue}` — gecikmiş kayıtta alan Özel Kalem'e de kapalıydı ve yeni
                termin ancak statü değişimindeki Gecikti-çıkış popup'ından girilebiliyordu (066/AK-12).
                Bu, termini düzeltmek için statü değiştirmeyi zorunlu kılıyordu; iki ayrı niyet birbirine
                bağlıydı. Uç bunu hiç kısıtlamıyordu (`update` yalnız `ACTION_MANAGE` ister, gecikme
                kapısı yok), yani kısıt baştan beri salt ekran tarafındaydı.

                Termin değişince NOT ZORUNLUDUR (analiz 7.8/7.12) — değişiklik izsiz kalmaz.
              */}
              <Calendar
                value={parseISODate(deadline)}
                minDate={parentDeadlineMin}
                dateFormat="dd.mm.yy"
                showIcon
                disabled={!deadlineEditable}
                onChange={(e) => setDeadline(toISODate(e.value ?? null))}
              />
            </label>
          )}

          {/* Stratejik Hedef · Katkı (127) — başlık/detayla aynı kural (infoEditable); alt görevde yok. */}
          <DetailClassificationFields
            visible={!isSubAction}
            editable={infoEditable}
            value={classification}
            onChange={setClassification}
          />

          {/* Aksiyon Notu (7.8) — yalnız metin; kronolojik, değiştirilemez geçmiş. Alt aksiyonda YOK (AK-7). */}
          {!isSubAction && (
            <ActionNotesSection
              notes={data?.notes ?? []}
              editable={editable}
              canManageNotes={notesManageable}
              note={note}
              noteDialogOpen={noteDialogOpen}
              deadlineChanged={deadlineChanged}
              pending={update.isPending}
              onNote={setNote}
              onNoteDialog={setNoteDialogOpen}
              onSubmitNote={submitUpdate}
              onSaveFields={onSaveFields}
              onEditNote={openEditNote}
              onDeleteNote={confirmNoteDelete}
            />
          )}
          {/* Not düzenle popup + sil onayı (ACTION_NOTE_MANAGE + sonlanmamış statü). */}
          {!isSubAction && notesManageable && (
            <>
              <NoteEditDialog
                open={editNote != null}
                text={editNoteText}
                pending={updateNoteMut.isPending}
                onText={setEditNoteText}
                onSubmit={submitEditNote}
                onClose={() => setEditNote(null)}
              />
              <ConfirmDialog group="note-delete" />
            </>
          )}
        </section>

        {/* Alt Aksiyonlar (7.17). Alt aksiyon detayında YOK — alt aksiyonlar iç içe geçmez. */}
        {!isSubAction && (
          <section className="minutes-section">
            <div className="minutes-section-head">
              <h2>{t('actions.detail.subActions.title')}</h2>
              {/*
                Koşul 2026-08-30'da yalnız ACTION_CREATE iken "ACTION_CREATE VEYA
                SUBACTION_CREATE" oldu (uçla birlikte, tek kaynak SUBACTION_CREATE_GRANTS).
                Eski hâlinde ACTION_MANAGE'i olup ACTION_CREATE'i olmayan rollerde (canlıda
                Yönetici) Ana Sorumlu butonu HİÇ görmüyordu — oysa aynı kişi alt görevi
                silebiliyordu. `editable` (= ACTION_MANAGE + sunucunun kayıt-düzeyi izni)
                korunur: yetki tek başına yetmez, kayıtla ilişki de gerekir.
              */}
              {editable && hasAnyGrant(...SUBACTION_CREATE_GRANTS) && (
                <Button
                  variant="secondary"
                  size="small"
                  icon="pi pi-plus"
                  label={t('actions.detail.subActions.add')}
                  onClick={() => setSubOpen(true)}
                  // Gecikmiş aksiyona alt aksiyon eklenemez (önce yeni termin verilmeli); 085/FR-008
                  // ile birlikte yalnız "Aksiyon Bekleniyor" statüsünde de eklenebilir.
                  disabled={isOverdue || !structurallyEditable}
                  tooltip={
                    !structurallyEditable && !isOverdue
                      ? t('actions.detail.subActions.onlyWhenPending', {
                          status: pendingStatusName,
                        })
                      : undefined
                  }
                />
              )}
            </div>
            {(data?.subActions?.length ?? 0) === 0 ? (
              <p className="act-empty-hint">{t('actions.detail.subActions.empty')}</p>
            ) : isMobile ? (
              <SubActionCards
                subActions={data?.subActions ?? []}
                showRecordNumber={showRecordNumber}
                editable={editable}
                // Parametre adı `id` DEĞİL: bu bileşende `id` zaten useParams()'tan gelen AÇILAN
                // görevin kimliğidir ve `aid`i besler. Aynı adı burada yeniden kullanmak, zaten
                // S3776 sınırındaki bir dosyada hangi `id`nin kapsamda olduğunu okunmaz kılıyordu
                // (Duo review bulgusu, MR !202).
                onOpen={(subActionId) => navigateWithOrigin(`/minutes/subactions/${subActionId}`)}
                onDelete={confirmSubDelete}
              />
            ) : (
              <DataTable
                value={data?.subActions ?? []}
                dataKey="id"
                paginator={false}
                className="minutes-participant-table"
                onRowClick={(e) =>
                  navigateWithOrigin(`/minutes/subactions/${(e.data as SubAction).id}`)
                }
                rowClassName={() => 'mtg-row-clickable'}
              >
                {showRecordNumber && (
                  <Column header={t('actions.columns.number')} field="recordNumber" />
                )}
                <Column
                  header={t('actions.columns.name')}
                  body={(s: SubAction) => (
                    <span className="minutes-cell-strong act-cell-clip" title={s.name}>
                      {s.name}
                    </span>
                  )}
                />
                {/* Açıklama (2026-09-10, kullanıcı isteği) — Tanım'ın yanında. Kırpılır: alan 2000
                    karaktere kadar çıkabilir ve satırı taşırırdı; tam metin title'da. */}
                <Column
                  header={t('actions.detail.subActions.description')}
                  body={(s: SubAction) => (
                    <span className="act-cell-desc" title={s.description ?? ''}>
                      {s.description ?? ''}
                    </span>
                  )}
                />
                {/* Atanan (066/AK-5 — alt aksiyonun tek atananı). Kolon başlığı "Atanan Kişi" (kullanıcı
                    isteği): ana liste "Sorumlu"dan ayrı, alt aksiyon namespace'inde kendi anahtarı. */}
                <Column
                  header={t('actions.detail.subActions.assignee')}
                  body={(s: SubAction) => s.assignee?.fullName ?? ''}
                />
                <Column
                  header={t('actions.columns.deadline')}
                  body={(s: SubAction) => (s.deadline ? formatDate(s.deadline) : '')}
                />
                <Column
                  header={t('actions.columns.status')}
                  headerStyle={{ minWidth: '10rem' }}
                  bodyStyle={{ minWidth: '10rem', whiteSpace: 'nowrap' }}
                  body={(s: SubAction) => (
                    <ActionStatusTag statusId={s.statusId} name={s.statusName ?? ''} />
                  )}
                />
                {/* 066/7.16 — Sil. Satır tıklaması alt detaya gider → butonda stopPropagation (aksi halde
                  onay yerine detaya giderdi).
                  <b>2026-09-11 (kullanıcı kararı):</b> kolon artık RECORD_DELETE'i olan kullanıcıda da
                  görünür (düzenlenebilir bağlam şartı onun için aranmaz) ve OTOMATİK alt görevlerde de
                  düğme çizilir — sunucu ikisini de siler, otomatikte üstteki Süreç Destek Yöneticisi
                  ataması da kalkar. Yalnız ACTION_MANAGE taşıyan kullanıcıda davranış 066'daki gibidir
                  (yalnız manuel). */}
                {subDeleteColumnVisible && (
                  <Column
                    header=""
                    headerStyle={{ width: '4rem' }}
                    bodyStyle={{ textAlign: 'center' }}
                    body={(s: SubAction) => {
                      if (!subDeleteVisible(s)) return null
                      const removeLabel = deleteLabel(t, 'actions.detail.subActions.delete', s.name)
                      return (
                        <button
                          type="button"
                          className="minutes-remove-btn"
                          aria-label={removeLabel}
                          title={removeLabel}
                          onClick={(ev) => {
                            ev.preventDefault()
                            ev.stopPropagation()
                            confirmSubDelete(s.id)
                          }}
                        >
                          <i className="pi pi-trash" aria-hidden="true" />
                        </button>
                      )
                    }}
                  />
                )}
              </DataTable>
            )}
            {/* Grup'a özel onay penceresi (yıkıcı işlem — Anayasa). */}
            <ConfirmDialog group="subaction-delete" />
          </section>
        )}

        {/* Dokümanlar (7.9): tablo (Ad·Tür·Boyut·Yükleyen·Yüklenme) — indir + (yetkili) yükle/kaldır (ACTION_FILE).
            İptal'de de indirilebilir (7.18).
            Buradaki `!isSubAction` koşulu ÖLÜ DALdır: alt görev id'si bu sayfaya hiç gelmez,
            yukarıda /minutes/subactions/:id'ye yönlendirilir. Alt görevin KENDİ doküman
            bölümü 2026-09-10'da eklendi (AK-7'nin doküman kolu kalktı) ve o sayfadadır. */}
        {!isSubAction && (
          <ActionDocumentsSection
            actionId={aid}
            documents={data?.documents ?? []}
            editable={documentsEditable}
          />
        )}
      </div>

      <SubActionDialog
        visible={subOpen}
        minDeadline={minDeadline}
        maxDeadline={parseISODate(data?.deadline ?? '') ?? undefined}
        saving={createSub.isPending}
        onSave={(body, files) =>
          createSub.mutate(
            { body, files },
            {
            // Alt görev her hâlükârda oluştu; ek adımı patladıysa (ya da yeni kayıt eşleştirilemediyse)
            // başarı DEĞİL uyarı gösterilir — dosya Alt Görev Detayı → Dokümanlar'dan eklenebilir.
            // Birden çok sorumlu seçildiyse (20.10.2026) her biri için bir alt görev açılır — metin sayıyı
            // söyler (i18n çoğul: tek kayıtta eski "Alt görev oluşturuldu." metni aynen kalır).
            onSuccess: ({ filesFailed, createdCount }) => {
              setSubOpen(false)
              const created = t('actions.detail.subActions.created', { count: createdCount })
              if (filesFailed) {
                toast.warn(created, t('actions.detail.subActions.filesFailed', { count: createdCount }))
              } else {
                toast.success(created)
              }
            },
            // Hatanın NEDENİ gösterilir (jenerik toast yerine): ör. ana aksiyon gecikmişse ne
            // yapılacağını söyleyen metin — bkz. subActionCreateErrorKey.
            onError: (err) =>
              toast.error(t(subActionCreateErrorKey(err), { status: pendingStatusName })),
            },
          )
        }
        onHide={() => setSubOpen(false)}
      />

      <OverdueExitDialog
        visible={exitTarget != null}
        noteRequired={isMainResponsible && !canManageStatus}
        deadline={exitDeadline}
        note={exitNote}
        minDate={overdueExitMinDate}
        pending={changeStatus.isPending}
        onDeadline={setExitDeadline}
        onNote={setExitNote}
        onSubmit={submitOverdueExit}
        onClose={() => setExitTarget(null)}
      />

      {/*
        105 — Onaya gönderme notu. Görev 331'e çekilirken araya girer; not ZORUNLUDUR (29.09.2026) ve
        pop-up iptal edilirse görev tamamlanmaz. Aynı bileşen alt görev ekranında da çizilir
        ({@code SubActionDetailPage}) — iki yüzeyin sınırı, sayacı ve uyarı metni ayrışamaz.
      */}
      <CompletionNoteDialog
        visible={completionNoteOpen}
        submitting={changeStatus.isPending}
        /* Dokümanlar bölümüne verilen ÖLÇÜTÜN AYNISI: dosya yükleme ucu statü ucundan farklı bir
           yetki ister (ACTION_MANAGE vs ACTION_STATUS_CHANGE). İki yüzey ayrışırsa kullanıcı
           pop-up'ta dosya seçer, statü yazılır ve yükleme 403 alıp dosyaları sessizce düşürür. */
        filesEnabled={documentsEditable}
        onSubmit={submitCompletionNote}
        onHide={closeCompletionNote}
      />

      {/* `AssigneeDeadlineDialog` KALDIRILDI (kullanıcı kuralı 2026-08-17): geciken aksiyona Yan Sorumlu
          eklemek artık hiç mümkün değil, dolayısıyla "yeni termin + gerekçe" soran bu pop-up'ın girdisi
          kalmadı. Kullanıcı uyarı alır ve önce hedef teslim tarihini günceller. */}

      <ActionHistoryDialog
        actionId={aid}
        visible={historyOpen}
        onHide={() => setHistoryOpen(false)}
      />
    </div>
  )
}

/** Salt-okuma Takip Edenler bölümünün zorunlu ama çağrılmayan geri çağrıları (120). */
function noop() {}
